import React, { useState, useEffect } from 'react';
import CookiesNotification from './CookiesNotification';

const LandingPage = ({ onSignUp, onLogin, setShowHowToStart }) => {
  const [capacity, setCapacity] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistResult, setWaitlistResult] = useState(null);
  const [waitlistError, setWaitlistError] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${window.location.origin}/api/system/capacity`)
      .then((r) => r.json())
      .then((d) => { if (alive && d && d.status === 'success') setCapacity(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const isFull = !!(capacity && capacity.is_full);

  const submitWaitlist = async (e) => {
    if (e) e.preventDefault();
    setWaitlistError('');
    setWaitlistSubmitting(true);
    try {
      const resp = await fetch(`${window.location.origin}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setWaitlistError(data.detail || 'Could not join the waitlist.');
      } else {
        setWaitlistResult(data);
      }
    } catch (err) {
      setWaitlistError('Network error. Please try again.');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Scarcity counter shown once the cap is enforced. Reveals the progress bar
  // only when it's credibly full-ish (>=40%) OR completely full, so an empty
  // launch doesn't read as "nobody uses this".
  const showCounter = !!(capacity && capacity.cap_enabled &&
    (capacity.pct_filled >= 40 || capacity.is_full));

  const ScarcityBar = () => {
    if (!showCounter) return null;
    return (
      <div className="max-w-md mx-auto mb-8" data-testid="scarcity-counter">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="text-gray-300 font-medium">
            {isFull ? 'All seats taken' : `Only ${capacity.seats_remaining} seats left`}
          </span>
          <span className="text-gray-400">{capacity.pct_filled}% full</span>
        </div>
        <div className="w-full h-2.5 bg-gray-700/70 rounded-full overflow-hidden border border-gray-600/50">
          <div
            className={`h-full rounded-full transition-all duration-700 ${isFull ? 'bg-red-500' : 'bg-gradient-to-r from-green-400 via-blue-500 to-purple-500'}`}
            style={{ width: `${Math.max(6, capacity.pct_filled)}%` }}
            data-testid="scarcity-progress"
          />
        </div>
      </div>
    );
  };

  const WaitlistForm = () => (
    <div
      className="max-w-md mx-auto bg-gray-800/80 border border-gray-700 rounded-xl p-6 mb-6"
      data-testid="waitlist-form"
    >
      {waitlistResult ? (
        <div className="text-center" data-testid="waitlist-success">
          <div className="text-green-400 text-3xl mb-2">✓</div>
          <p className="text-white font-semibold mb-1">You're on the list!</p>
          <p className="text-gray-300 text-sm">
            You're <span className="font-bold text-blue-400">#{waitlistResult.position}</span> in line.
            We'll email you the moment a seat opens up.
          </p>
        </div>
      ) : (
        <form onSubmit={submitWaitlist}>
          <p className="text-white font-semibold mb-1 text-center">We're at capacity right now</p>
          <p className="text-gray-400 text-sm mb-4 text-center">
            Join the waitlist and we'll notify you the second a seat frees up.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              value={waitlistEmail}
              onChange={(e) => setWaitlistEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              data-testid="waitlist-email-input"
            />
            <button
              type="submit"
              disabled={waitlistSubmitting}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
              data-testid="waitlist-submit-button"
            >
              {waitlistSubmitting ? 'Joining…' : 'Join Waitlist'}
            </button>
          </div>
          {waitlistError && (
            <p className="text-red-400 text-sm mt-2 text-center" data-testid="waitlist-error">{waitlistError}</p>
          )}
        </form>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header Navigation */}
      <nav className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="text-2xl font-bold flex items-center">
                <span className="text-blue-400 mr-2">📈</span>
                actuvo.ai
              </div>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-300 hover:text-white transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-gray-300 hover:text-white transition-colors">
                Pricing
              </a>
              <a href="#how-it-works" className="text-gray-300 hover:text-white transition-colors">
                How it Works
              </a>
              <button onClick={() => setShowHowToStart(true)} className="text-gray-300 hover:text-white transition-colors bg-transparent border-none cursor-pointer" data-testid="nav-how-to-start-button">
                How to Start
              </button>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-4">
              <button
                onClick={onLogin}
                className="text-gray-300 hover:text-white font-medium transition-colors"
                data-testid="nav-login-button"
              >
                Log In
              </button>
              <button
                onClick={onSignUp}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-semibold transition-colors"
                data-testid="nav-signup-button"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">Dynamic </span>
            <span className="text-blue-400">Grid Trading</span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
            Trade the math, not the noise. Our Dynamic Grid Bot places maker-only orders
            on Hyperliquid to capture volatility while reducing taker fees.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            {isFull ? (
              <a
                href="#waitlist"
                className="bg-gradient-to-r from-red-500 to-purple-600 hover:from-red-600 hover:to-purple-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
                data-testid="hero-join-waitlist-button"
              >
                Join the Waitlist
              </a>
            ) : (
              <button
                onClick={onSignUp}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
                data-testid="hero-get-started-button"
              >
                Get Started — $30/month
              </button>
            )}
          </div>

          <ScarcityBar />

          {isFull && (
            <div id="waitlist">
              <WaitlistForm />
            </div>
          )}

          <div
            className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/40 text-blue-200 text-sm font-medium px-4 py-2 rounded-full mb-6"
            data-testid="hero-low-entry-badge"
          >
            <span className="text-green-400">●</span>
            Try the bot live with as little as <span className="font-bold text-white">$20</span> — subscription still required.
          </div>

          <div className="text-gray-400 text-sm">
            Your keys, your control • Cancel anytime • No performance fees
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose actuvo.ai?</h2>
            <p className="text-base md:text-lg text-gray-400 max-w-3xl mx-auto">
              Mathematical edge meets market structure. Dynamic grid trading captures volatility on Hyperliquid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 - Grid Trading */}
            <div className="bg-gray-700 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">&#x25A6;</div>
              <h3 className="text-xl font-bold mb-3">Dynamic Grid Trading</h3>
              <p className="text-gray-400">
                Places buy/sell limit orders around the current price. Profits from every completed round trip — automatically.
              </p>
            </div>

            {/* Feature 2 - Maker Rebates */}
            <div className="bg-gray-700 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">&#x21C4;</div>
              <h3 className="text-xl font-bold mb-3">Maker-Only Orders</h3>
              <p className="text-gray-400">
                All orders use ALO (Add Liquidity Only) to reduce taker fees on Hyperliquid. Negative maker rebates may apply if your Hyperliquid account qualifies for the rebate tier.
              </p>
            </div>

            {/* Feature 3 - Risk Controls */}
            <div className="bg-gray-700 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="text-xl font-bold mb-3">Hard Kill Switches</h3>
              <p className="text-gray-400">
                90% equity floor, 2% max single loss, automatic halt after 10 losses. No exceptions.
              </p>
            </div>

            {/* Feature 4 - Full Transparency */}
            <div className="bg-gray-700 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">&#x1F4CA;</div>
              <h3 className="text-xl font-bold mb-3">Full Transparency</h3>
              <p className="text-gray-400">
                Live grid visualization shows every level, fill, and P&L in real time on your dashboard.
              </p>
            </div>

            {/* Feature 5 - 24/7 Operation */}
            <div className="bg-gray-700 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🕒</div>
              <h3 className="text-xl font-bold mb-3">24/7 Monitoring</h3>
              <p className="text-gray-400">
                Monitors your grid levels, open orders, and position health around the clock.
              </p>
            </div>

            {/* Feature 6 - Maker Only */}
            <div className="bg-gray-700 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-bold mb-3">Maker-Only Execution</h3>
              <p className="text-gray-400">
                Never crosses the spread. Earns rebates instead of paying taker fees.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-base md:text-lg text-gray-400">
              Get started in 3 simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Connect Your Account</h3>
              <p className="text-gray-400">
                Create an account and connect your trade-only API key. 
                Your funds stay in your own Hyperliquid wallet.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Start Trading</h3>
              <p className="text-gray-400">
                Activate the Grid Bot and monitor live. 
                Watch grid levels fill and verify the strategy works for you.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Go Live</h3>
              <p className="text-gray-400">
                The grid bot rebalances automatically and trades 24/7.
                Maker-only execution with hard risk controls.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-base md:text-lg text-gray-400">
              One plan, full access. No hidden fees.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="bg-gradient-to-br from-blue-900 to-purple-900 border-2 border-blue-500 rounded-xl p-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">Grid Bot Pro</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-blue-400">$30</span>
                  <span className="text-gray-400 text-xl">/month</span>
                </div>
                
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-center">
                    <span className="text-green-400 mr-3">✓</span>
                    Live grid level monitoring
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-3">✓</span>
                    Automated signal execution
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-3">✓</span>
                    Risk controls (2% stop loss, trailing TP)
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-3">✓</span>
                    Real-time dashboard
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-3">✓</span>
                    Cancel anytime
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-3">✓</span>
                    No performance fees
                  </li>
                </ul>
                
                <button
                  onClick={onSignUp}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg mb-4"
                  data-testid="pricing-get-started-button"
                >
                  Get Started
                </button>
                
                <div className="text-gray-400 text-sm mb-2">
                  Instant activation • No performance fees
                </div>
                <div className="text-blue-300 text-xs font-medium" data-testid="pricing-low-entry-note">
                  Test live performance with as little as <span className="text-white font-bold">$20</span> — subscription still required.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Grid Trading?
          </h2>
          <p className="text-base md:text-lg mb-8 opacity-90">
            Connect your Hyperliquid account and let the grid bot work for you.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onSignUp}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg transition-colors"
              data-testid="cta-get-started-button"
            >
              Get Started
            </button>
            <button
              onClick={onLogin}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              data-testid="cta-login-button"
            >
              Already Have Account? Log In
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-2xl font-bold flex items-center justify-center mb-4">
              <span className="text-blue-400 mr-2">📈</span>
              actuvo.ai
            </div>
            <p className="text-gray-400 mb-6">
              Grid trading bot for Hyperliquid perpetuals
            </p>
            <div className="text-gray-500 text-sm">
              © 2026 actuvo.ai. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
      
      {/* Cookies Notification */}
      <CookiesNotification />
    </div>
  );
};

export default LandingPage;
